/**
 * Brand Safety Severity helpers
 *
 * Pure, immutable utilities for reading and updating brand safety preferences
 * that now carry a per-severity structure while remaining backward compatible
 * with the legacy flat boolean map (`{ AlcoholFlag: true }`).
 *
 * A single flag's value may be either:
 *   - a legacy boolean (`true` => all severities on, `false` => all off), or
 *   - a severity object `{ severe, moderate, low, minor }`.
 *
 * These helpers normalize any accepted shape into severity objects so the rest
 * of the UI can treat the data uniformly.
 *
 * @module components/Persona/BrandSafety/brandSafetySeverity
 */

import { BRAND_SAFETY_SEVERITY_KEYS, BRAND_SAFETY_SEVERITY_FLAG_KEYS } from './brandSafetyConsts';

/**
 * @typedef {Object.<string, boolean>} SeverityState
 * A map of severity key (`severe`|`moderate`|`low`|`minor`) to enabled boolean.
 */

/**
 * @typedef {Object.<string, (boolean|SeverityState)>} BrandSafetyPreferences
 * A map of flag key (e.g. `AlcoholFlag`) to either a legacy boolean or a
 * SeverityState object.
 */

/**
 * Build a SeverityState with every severity set to `value`.
 * @param {boolean} value
 * @returns {SeverityState}
 */
function buildSeverityState(value) {
  return BRAND_SAFETY_SEVERITY_KEYS.reduce((acc, key) => {
    acc[key] = value;
    return acc;
  }, {});
}

/**
 * Normalize a single flag value (boolean | partial object | undefined) into a
 * complete SeverityState. Missing keys default to `true` when the source is
 * absent, otherwise to `false` so an explicit partial object is respected.
 * @param {boolean|SeverityState|undefined|null} value
 * @returns {SeverityState}
 */
export function normalizeFlagValue(value) {
  if (value === true || value === undefined || value === null) {
    return buildSeverityState(true);
  }
  if (value === false) {
    return buildSeverityState(false);
  }
  if (typeof value === 'object') {
    return BRAND_SAFETY_SEVERITY_KEYS.reduce((acc, key) => {
      acc[key] = Boolean(value[key]);
      return acc;
    }, {});
  }
  // Unknown scalar (e.g. 0/1) -> coerce to boolean semantics.
  return buildSeverityState(Boolean(value));
}

/**
 * Normalize a whole preferences map into `{ flagKey: SeverityState }`. Accepts a
 * legacy boolean map, a mixed map, a severity map, or an empty/undefined value
 * (which yields the default: every flag with all severities on).
 * @param {BrandSafetyPreferences|undefined|null} prefs
 * @returns {Object.<string, SeverityState>}
 */
export function normalizePreferences(prefs) {
  const source = prefs && typeof prefs === 'object' ? prefs : {};
  return BRAND_SAFETY_SEVERITY_FLAG_KEYS.reduce((acc, flagKey) => {
    acc[flagKey] = normalizeFlagValue(source[flagKey]);
    return acc;
  }, {});
}

/**
 * The default preferences: every managed flag with all severities enabled.
 * @returns {Object.<string, SeverityState>}
 */
export function getDefaultPreferences() {
  return normalizePreferences({});
}

/**
 * Whether every *selectable* severity of a single flag is enabled. A `locked` map
 * (`{ [severity]: true }`, e.g. from a campaign ceiling) excludes those severities from the check, so a
 * category whose only-off severities are all campaign-locked still reads as "all on". Returns false when
 * every severity is locked (nothing selectable). With no `locked` map this is the original all-four check.
 * @param {SeverityState} severity
 * @param {Object.<string, boolean>|null} [locked]
 * @returns {boolean}
 */
export function isCategoryAllOn(severity, locked = null) {
  const state = normalizeFlagValue(severity);
  const keys = BRAND_SAFETY_SEVERITY_KEYS.filter((key) => !locked?.[key]);
  if (!keys.length) return false;
  return keys.every((key) => state[key]);
}

/**
 * Whether at least one *selectable* (non-locked) severity of a single flag is enabled. Drives the
 * indeterminate state of the per-category select-all checkbox (some-but-not-all selected).
 * @param {SeverityState} severity
 * @param {Object.<string, boolean>|null} [locked]
 * @returns {boolean}
 */
export function isCategorySomeOn(severity, locked = null) {
  const state = normalizeFlagValue(severity);
  return BRAND_SAFETY_SEVERITY_KEYS.some((key) => !locked?.[key] && state[key]);
}

/**
 * Whether every severity of every managed flag is enabled.
 * @param {BrandSafetyPreferences} prefs
 * @returns {boolean}
 */
export function isAllOn(prefs) {
  const normalized = normalizePreferences(prefs);
  return BRAND_SAFETY_SEVERITY_FLAG_KEYS.every((flagKey) => isCategoryAllOn(normalized[flagKey]));
}

/**
 * Immutably set a single severity of a single flag.
 * @param {BrandSafetyPreferences} prefs
 * @param {string} flagKey
 * @param {string} severityKey
 * @param {boolean} checked
 * @returns {Object.<string, SeverityState>} a new normalized preferences map
 */
export function setSeverity(prefs, flagKey, severityKey, checked) {
  const normalized = normalizePreferences(prefs);
  return {
    ...normalized,
    [flagKey]: {
      ...normalized[flagKey],
      [severityKey]: Boolean(checked),
    },
  };
}

/**
 * Immutably set the severities of a single flag. A `locked` map (`{ [severity]: true }`) protects those
 * severities: they keep their current value instead of being flipped, so a per-category Select All /
 * Unselect All can never turn a campaign-locked leaf on or off. With no `locked` map every severity is
 * set (original behavior).
 * @param {BrandSafetyPreferences} prefs
 * @param {string} flagKey
 * @param {boolean} checked
 * @param {Object.<string, boolean>|null} [locked]
 * @returns {Object.<string, SeverityState>} a new normalized preferences map
 */
export function setFlagAll(prefs, flagKey, checked, locked = null) {
  const normalized = normalizePreferences(prefs);
  const current = normalized[flagKey];
  const next = BRAND_SAFETY_SEVERITY_KEYS.reduce((acc, key) => {
    acc[key] = locked?.[key] ? Boolean(current[key]) : Boolean(checked);
    return acc;
  }, {});
  return {
    ...normalized,
    [flagKey]: next,
  };
}

/**
 * Immutably set every severity of every managed flag.
 * @param {BrandSafetyPreferences} prefs
 * @param {boolean} checked
 * @returns {Object.<string, SeverityState>} a new normalized preferences map
 */
export function setAll(prefs, checked) {
  return BRAND_SAFETY_SEVERITY_FLAG_KEYS.reduce((acc, flagKey) => {
    acc[flagKey] = buildSeverityState(Boolean(checked));
    return acc;
  }, {});
}

/**
 * Derive the legacy flat boolean map from severity preferences. A flag is `true`
 * when any of its severities is enabled. Provided as a backward-compat escape
 * hatch for consumers that still expect `{ AlcoholFlag: boolean }`.
 * @param {BrandSafetyPreferences} prefs
 * @returns {Object.<string, boolean>}
 */
export function toLegacyBooleanMap(prefs) {
  const normalized = normalizePreferences(prefs);
  return BRAND_SAFETY_SEVERITY_FLAG_KEYS.reduce((acc, flagKey) => {
    acc[flagKey] = BRAND_SAFETY_SEVERITY_KEYS.some((key) => normalized[flagKey][key]);
    return acc;
  }, {});
}
